/**
 * Pure utility functions for the My Leave ESS screen.
 *
 * Extracted from my_leave_screen.tsx per the >150-line component rule
 * (CLAUDE.md § Component Testability): calculations, validation, and
 * sorting logic are pure-ts so they are unit-testable without React.
 *
 * Days are stored × 100 in the backend; all functions here take the scaled
 * integer directly and only divide at the last moment to avoid accumulating
 * float precision drift across multiple operations.
 */

import type { TFunction } from '@mukil/i18n';
import type { LeaveStatus } from '../../data/wagepro_store_leave.js';
import type { IsoDate } from '../../data/types.js';
import { format_leave_days } from '../../lib/format.js';

// ── Balance card derivation ───────────────────────────────────────────────

/**
 * One balance row from `listMyLeaveBalances`, narrowed to the fields the
 * screen needs. Locally declared — we never import from apps/api directly.
 */
export interface MyLeaveBalance {
  leave_type: string;
  balance_days_scaled: number;
  /**
   * Everything ever granted: balance + taken + booked + paid out (same as
   * admin Balances).
   */
  total_days_scaled: number;
  /** Approved leave that debited the balance, on or before today. */
  taken_days_scaled: number;
  /** Approved leave that debited the balance, still in the future. */
  booked_days_scaled: number;
  pending_days_scaled: number;
  /** balance − pending. */
  available_days_scaled: number;
}

/**
 * The balance row for a given leave type code, or null when that type does
 * not track a balance for this employee (e.g. unpaid leave).
 */
export function find_balance_for_type(
  balances: readonly MyLeaveBalance[],
  leave_type: string,
): MyLeaveBalance | null {
  if (leave_type === '') return null;
  return balances.find((b) => b.leave_type === leave_type) ?? null;
}

/**
 * Segment widths (0–100 %) for the balance meter: taken, then booked, then
 * pending.
 */
export interface LeaveBalanceMeter {
  taken_pct: number;
  booked_pct: number;
  pending_pct: number;
}

/**
 * How much of the Total has been taken, how much is booked for later, and how
 * much is waiting on approval, as percentages of Total. The unfilled remainder
 * of the track is what the employee still has. Clamped in that order so an
 * overdrawn balance never overflows the bar.
 */
export function leave_balance_meter(
  balance: MyLeaveBalance,
): LeaveBalanceMeter {
  const total = balance.total_days_scaled;
  if (total <= 0) return { taken_pct: 0, booked_pct: 0, pending_pct: 0 };
  const pct = (v: number): number => (Math.max(v, 0) / total) * 100;
  const taken_pct = Math.min(pct(balance.taken_days_scaled), 100);
  const booked_pct = Math.min(pct(balance.booked_days_scaled), 100 - taken_pct);
  const pending_pct = Math.min(
    pct(balance.pending_days_scaled),
    100 - taken_pct - booked_pct,
  );
  return { taken_pct, booked_pct, pending_pct };
}

/**
 * Locale-formatted day count from a ×100 scaled value, via the same
 * `format_leave_days` the admin Balances tab uses.
 */
export function format_days_for_display(
  scaled: number,
  locale: string,
): string {
  return format_leave_days(format_days_scaled(scaled), locale);
}

/**
 * One active leave type entry from `listMyLeaveBalances`'s `leave_types`
 * array — what the request form's type picker populates.
 */
export interface MyLeaveType {
  code: string;
  label: string;
}

/**
 * One request row from `listMyLeaveRequests`.
 */
export interface MyLeaveRequest {
  id: string;
  leave_type: string;
  start_date: IsoDate;
  end_date: IsoDate;
  days_requested: number;
  status: string;
  notes: string | null;
  rejection_reason: string | null;
  created_at: string;
}

/** Days × 100 → display number with up to 1 decimal place. */
export function format_days_scaled(scaled: number): number {
  return scaled / 100;
}

/**
 * Classify a raw leave status string, defaulting to 'pending' for unknown
 * values (mirrors wagepro_store_leave.ts's normaliseLeaveStatus).
 *
 * `'cancelled'` MUST be in this list — without it a cancelled request is
 * treated as `'pending'`, showing a live Cancel button whose click 400s.
 * (Adversarial-review finding B3.)
 */
export function normalise_leave_status(raw: string): LeaveStatus {
  const STATUSES: readonly string[] = [
    'pending',
    'approved',
    'rejected',
    'cancelled',
  ];
  return STATUSES.includes(raw) ? (raw as LeaveStatus) : 'pending';
}

/**
 * Pass the raw leave-type code through unchanged (mirrors the fix in
 * wagepro_store_leave.ts's normaliseLeaveType — adversarial-review S6).
 * The screen renders via `t(key, { defaultValue: code })` so every code
 * gets its own label rather than silently collapsing to "Other".
 */
export function normalise_leave_type(raw: string): string {
  return raw;
}

// ── Form validation ──────────────────────────────────────────────────────────

export interface LeaveRequestFormValues {
  leave_type: string;
  start_date: string;
  end_date: string;
  notes: string;
}

export type LeaveRequestFormError =
  | 'leave_type_required'
  | 'start_date_required'
  | 'end_date_required'
  | 'end_before_start';

/**
 * Validates a leave-request form, returning the first blocking error or
 * `null` when the form is submittable.
 */
export function validate_leave_request_form(
  values: LeaveRequestFormValues,
): LeaveRequestFormError | null {
  if (!values.leave_type) return 'leave_type_required';
  if (!values.start_date) return 'start_date_required';
  if (!values.end_date) return 'end_date_required';
  if (values.end_date < values.start_date) return 'end_before_start';
  return null;
}

/**
 * The translated message for a `LeaveRequestFormError`. Takes `t` as a
 * parameter rather than the caller building the key via a template literal
 * (`t(\`my_leave.form.error.${key}\`)`, the previous shape in
 * `my_leave_request_form.tsx`) — a template-literal argument is DYNAMIC as
 * far as the static i18n key extractor is concerned (its regex requires a
 * plain string literal immediately after `t(`), so every one of these four
 * keys was misreported as an orphan despite being genuinely reachable at
 * runtime. See `leave_utils.ts`'s `leave_action_error_message` for the same
 * pattern applied to the employer-side leave error keys.
 */
export function leave_request_form_error_message(
  t: TFunction,
  error: LeaveRequestFormError,
): string {
  switch (error) {
    case 'leave_type_required':
      return t('my_leave.form.error.leave_type_required');
    case 'start_date_required':
      return t('my_leave.form.error.start_date_required');
    case 'end_date_required':
      return t('my_leave.form.error.end_date_required');
    case 'end_before_start':
      return t('my_leave.form.error.end_before_start');
  }
}

/**
 * Derives the `days_requested` scaled integer (×100) from two ISO date strings.
 * The calculation counts INCLUSIVE calendar days (end_date − start_date + 1),
 * then multiplies by 100.  Returns `null` if either date is unparseable or
 * end < start.
 */
export function derive_days_requested_scaled(
  start_date: string,
  end_date: string,
): number | null {
  const start = new Date(start_date);
  const end = new Date(end_date);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
  const diff_ms = end.getTime() - start.getTime();
  if (diff_ms < 0) return null;
  const MS_PER_DAY = 86_400_000;
  const inclusive_days = Math.floor(diff_ms / MS_PER_DAY) + 1;
  return inclusive_days * 100;
}

// ── Request list helpers ──────────────────────────────────────────────────────

/**
 * Sort requests most-recent first, breaking ties by id descending (stable
 * across re-renders, matching the backend's `orderBy(desc(created_at))`).
 */
export function sort_requests_desc(
  requests: readonly MyLeaveRequest[],
): MyLeaveRequest[] {
  return [...requests].sort((a, b) => {
    const by_date = b.created_at.localeCompare(a.created_at);
    if (by_date !== 0) return by_date;
    return b.id.localeCompare(a.id);
  });
}

// `leave_status_to_pill` was deleted in the adversarial-review S1 fix.
// Use `LEAVE_STATUS_PILL` from `../../lib/label_keys.ts` directly — it is
// the single canonical status→pill mapping for both the employer queue and
// the ESS screen, so the two screens can never drift from each other.
